/**
 * @packageDocumentation
 * @module      CorThemeSelector.ts
 * @description Encapsulates the persistence of the user's theme choice.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorThemeSelector_1;
import { LitElement, html, customElement, internalProperty } from 'lit-element';
/**
 * Encapsulates the persistence of the user's theme choice.
 *
 * This class is not exported.
 */
class CorThemeNameStorage {
    static set themeName(val) {
        sessionStorage.setItem(this.sessionStorageKeyForTheme, val);
    }
    static get themeName() {
        var _a;
        return (_a = sessionStorage.getItem(this.sessionStorageKeyForTheme)) !== null && _a !== void 0 ? _a : ThemeName.Light;
    }
    static clear() {
        sessionStorage.removeItem(this.sessionStorageKeyForTheme);
    }
}
CorThemeNameStorage.sessionStorageKeyForTheme = "theme";
/** Enumerates the available themes. These choices are defined in Shui. */
export var ThemeName;
(function (ThemeName) {
    ThemeName["Light"] = "light";
    ThemeName["Dark"] = "dark";
})(ThemeName || (ThemeName = {}));
/**
 * A component by which the user may choose a visual theme.
 *
 * This class inserts the chosen theme as a theme attribute in the html tag of the document.
 * Shui's CSS selectors (shui-styles.css) are then based on html[theme="light"] or html[theme="dark"]
 */
let CorThemeSelector = CorThemeSelector_1 = class CorThemeSelector extends LitElement {
    /** Set the theme in the HTML document and also in persistent storage. */
    static setTheme(themeName) {
        CorThemeNameStorage.themeName = themeName;
        const htmlElement = document.getElementsByTagName("html")[0];
        let themeAttr = document.createAttribute("theme");
        themeAttr.value = themeName;
        htmlElement.attributes.setNamedItem(themeAttr);
    }
    static getTheme() {
        return CorThemeNameStorage.themeName;
    }
    /** Set the theme in the HTML document to the one in persistent storage. */
    static alignThemeWithStorage() {
        CorThemeSelector_1.setTheme(CorThemeNameStorage.themeName);
    }
    /** Clear the theme, thus returning to the default (Light) theme. */
    clearTheme() {
        CorThemeNameStorage.clear();
        document.getElementsByTagName('html')[0].removeAttribute('theme');
    }
    /** Tell whether the theme is dark (true) or light (false). */
    get isDark() {
        return CorThemeNameStorage.themeName === ThemeName.Dark;
    }
    /** Toggle the theme between dark and light. */
    toggleTheme() {
        CorThemeSelector_1.setTheme(CorThemeNameStorage.themeName === ThemeName.Light ? ThemeName.Dark : ThemeName.Light);
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        return html `<sh-toggle label='Dark theme' ?active=${this.isDark} @click=${this.toggleTheme}></sh-toggle>`;
    }
};
__decorate([
    internalProperty()
], CorThemeSelector.prototype, "isDark", null);
CorThemeSelector = CorThemeSelector_1 = __decorate([
    customElement('cor-theme-selector')
], CorThemeSelector);
export { CorThemeSelector };
//# sourceMappingURL=CorThemeSelector.js.map